import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';

import { Icons } from '../../../components';
import css from './SectionFAQ.module.css';

const FAQ_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8'];

const parseAnswerText = answer => {
  const lines = String(answer || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  const blocks = [];
  let listItems = [];

  lines.forEach(line => {
    if (line.startsWith('- ')) {
      listItems.push(line.replace(/^- /, '').trim());
      return;
    }

    if (listItems.length) {
      blocks.push({ type: 'list', items: listItems });
      listItems = [];
    }

    blocks.push({ type: 'paragraph', text: line });
  });

  if (listItems.length) {
    blocks.push({ type: 'list', items: listItems });
  }

  return blocks;
};

function SectionFAQ() {
  const intl = useIntl();
  const [openIndex, setOpenIndex] = useState(null);

  const items = useMemo(() => {
    return FAQ_KEYS.map(key => {
      const question = intl.formatMessage({ id: `Homepage.FAQ.${key}.question` });
      const answer = intl.formatMessage({ id: `Homepage.FAQ.${key}.answer` });

      return {
        key,
        question,
        answerBlocks: parseAnswerText(answer),
      };
    });
  }, [intl]);

  return (
    <section className={css.sectionFaq}>
      <div className={css.fixedWidthContainer}>
        <div className={css.sectionHead}>
          <h2>{intl.formatMessage({ id: 'Homepage.FAQ.title' })}</h2>
        </div>

        <div className={css.accordion}>
          {items.map((item, index) => {
            const isOpen = openIndex === index;
            const triggerId = `homepage-faq-trigger-${index}`;
            const panelId = `homepage-faq-panel-${index}`;

            return (
              <div key={item.key} className={classNames(css.item, { [css.itemOpen]: isOpen })}>
                <button
                  id={triggerId}
                  type="button"
                  className={css.trigger}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpenIndex(current => (current === index ? null : index))}
                >
                  <span className={css.question}>{item.question}</span>
                  <span className={classNames(css.chevron, { [css.chevronOpen]: isOpen })}>
                    <Icons name="downArrow" />
                  </span>
                </button>

                {isOpen ? (
                  <div id={panelId} role="region" aria-labelledby={triggerId} className={css.panel}>
                    {item.answerBlocks.map((block, blockIndex) => {
                      if (block.type === 'list') {
                        return (
                          <ul key={`${item.key}-list-${blockIndex}`} className={css.answerList}>
                            {block.items.map((listItem, listIndex) => (
                              <li
                                key={`${item.key}-list-item-${listIndex}`}
                                className={css.answerListItem}
                              >
                                {listItem}
                              </li>
                            ))}
                          </ul>
                        );
                      }

                      return (
                        <p
                          key={`${item.key}-paragraph-${blockIndex}`}
                          className={css.answerParagraph}
                        >
                          {block.text}
                        </p>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default SectionFAQ;
